import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import type React from 'react';
import type { ComponentProps } from 'react';

import { cn } from '@/lib/utils.js';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-full text-sm font-medium transition-all duration-200 ease-[cubic-bezier(0.22,1,0.36,1)] outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*=size-])]:size-4 [&_svg]:shrink-0 active:scale-[0.97]',
  {
    variants: {
      variant: {
        // `border border-transparent` is permanent so hover can swap to
        // `border-primary` without adding a 1px ring that nudges the layout.
        default:
          'border border-transparent bg-primary text-primary-foreground hover:bg-transparent hover:text-primary hover:border-primary',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline:
          'border border-input bg-background hover:bg-muted hover:text-foreground hover:border-border',
        secondary:
          'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-muted hover:text-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
        // brand = solid primary fill. Same reserved transparent border as
        // `default` to prevent hover-jump.
        brand:
          'border border-transparent bg-primary text-primary-foreground hover:bg-transparent hover:text-primary hover:border-primary',
        // brand-outline = transparent bg, primary border + text; fills on hover.
        'brand-outline':
          'border border-primary/40 bg-transparent text-primary hover:bg-primary hover:text-primary-foreground hover:border-primary',
        // destructive-outline = transparent bg, destructive border + text; fills on hover.
        'destructive-outline':
          'border border-destructive/40 bg-transparent text-destructive hover:bg-destructive hover:text-destructive-foreground hover:border-destructive',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3 text-xs',
        lg: 'h-11 px-6 text-base',
        xl: 'h-12 px-8 text-base',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends ComponentProps<'button'>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: ButtonProps): React.ReactNode {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}

export { buttonVariants };
